
import { useState, useEffect } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  courseMembersQuery,
  gamesQuery,
  gamesSessionsQuery,
  getItems,
  getClassificationScoresInsights,
  getNormalScoresInsights,
  getClassificationDecisionsInsights,
  getNormalDecisionsInsights
} from '../api';

import './styles.css';

import { navigation } from '../data';
import SubPagesLayout from '../../../UI/layout/subPages';
import PopupMessages from '../../../UI/introGameLayout/popupMessages';
import Scores from '../../../UI/game/scores';
import InstructorClassifyingNormalModal from '../../../UI/instructor/decisionInsights/classifyingNormalModal';

import {
  classifyingNormalSections,
  popupMessagesDecisionInsightsClassifyingNormal,
  popupMessagesDecisionInsightsNoGamesData,
  classifyingNormalElementsHeaders,
  classifyingNormalAllOtherAccountsHeaders,
  classifyingNormalElementsOtherAccountsNormalHeaders
} from './data';

export default function InstructorInsightsClassifyingNormal() {
  const navigate = useNavigate();

  const [isModalShown, setIsModalShown] = useState(false);
  const [isDetailsButtonLoading, setIsDetailsButtonLoading] = useState(false);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);
  const [filterBy, setFilterBy] = useState('Accuracy');

  const [popupMessages, setPopupMessages] = useState([]);
  const [detailsButtonText, setDetailsButtonText] = useState(null);
  const [labels, setLabels] = useState([]);

  const [classificationGameIds, setClassificationGameIds] = useState([]);
  const [normalGameIds, setNormalGameIds] = useState([]);

  const [data, setData] = useState([]);
  const [elementsData, setElementsData] = useState([]);
  const [allOtherAccountsData, setAllOtherAccountsData] = useState([]);
  const [elementsOtherAccountsNormalData, setElementsOtherAccountsNormalData] = useState([]);

  const filterByOptions = ['Accuracy', 'Points'];

  const { data: courseMembers } = useQuery(courseMembersQuery()) || {};

  const { data: games } = useQuery(gamesQuery()) || {};
  const { data: gamesSessions } = useQuery(gamesSessionsQuery()) || {};

  const { data: items } = useQuery({
    queryKey: ['items'],
    queryFn: async () => getItems(),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: shouldFetchDetails,
  });

  const { data: classificationScoresInsights } = useQuery({
    queryKey: ['classificationScoresInsightsOverall'],
    queryFn: async () => getClassificationScoresInsights({completed: true}),
    refetchOnWindowFocus: false,
  });

  const { data: normalScoresInsights } = useQuery({
    queryKey: ['normalScoresInsightsOverall'],
    queryFn: async () => getNormalScoresInsights({completed: true}),
    refetchOnWindowFocus: false,
  });

  const classificationDecisionsQueries = useQueries({
    queries: classificationGameIds.map(gameId => ({
      queryKey: ['classificationDecisionsInsights', gameId],
      queryFn: () => getClassificationDecisionsInsights({ gameId }),
      refetchOnWindowFocus: false,
    }))
  });

  const normalDecisionsQueries = useQueries({
    queries: normalGameIds.map(gameId => ({
      queryKey: ['normalDecisionsInsights', gameId],
      queryFn: () => getNormalDecisionsInsights({ gameId }),
      refetchOnWindowFocus: false,
    }))
  });

  useEffect(() => {
    if(classificationScoresInsights && normalScoresInsights && courseMembers?.length > 0) {
      formatLeaderboardData();
    }
  }, [classificationScoresInsights, normalScoresInsights, filterBy, courseMembers]);

  const calculateAverage = (data, key, prop) => {
    if (!data || data.length === 0) return 0;

    let sum = 0;
    let count = 0;

    data.forEach(record => {
      const value = record[key]?.[prop];
      if (value !== null && value !== undefined) {
        sum += value;
        count++;
      }
    });

    return count > 0 ? sum / count : 0;
  };

  const getScoreData = (scores, filterKey, prop, color) => {
    const score = calculateAverage(scores, filterKey, prop);
      return {
        score: score * 100,
        color
      };
  };

  const formatLeaderboardData = () => {
    const filterKey = filterBy === 'Accuracy' ? 'accuracy' : 'points';

    const instructorIds = new Set(
      courseMembers
        .filter(member => member.is_instructor)
        .map(member => member.user_id)
    );
    console.log('classificationScoresInsights', classificationScoresInsights);
    const filteredClassificationScores = classificationScoresInsights.filter(score => !instructorIds.has(score.user));
    const filteredNormalScores = normalScoresInsights.filter(score => !instructorIds.has(score.user));

    const data = classifyingNormalSections
      .map(section => {
        return {
          title: section.title,
          bars: section.bars.map(bar => ({
            title: bar.title,
            lines: [
              getScoreData(filteredClassificationScores, filterKey, bar.key, '#D7F0F2'),
              getScoreData(filteredNormalScores, filterKey, bar.key, '#FFF8EB')
            ]
          })),
          barsLabel: '%'
        }
      })
      .filter(section => 
        section.bars.some(bar => 
          bar.lines.some(line => line.score > 0)
        )
      );
    
    setData(data);
  }
    
  useEffect(() => {
    if (games && shouldFetchDetails) {
      const classificationGames = games.filter(game => game.game_type === 'Account Classification');
      const normalGames = games.filter(game => game.game_type === 'Normal Balance');

      const classificationGameIds = classificationGames.map(game => game.id);
      const normalGameIds = normalGames.map(game => game.id);
      setClassificationGameIds(classificationGameIds);
      setNormalGameIds(normalGameIds);
      setShouldFetchDetails(false);
    }
  }, [games, shouldFetchDetails]);

  useEffect(() => {
    if (games?.length > 0 && gamesSessions && courseMembers?.length > 0) {
      const instructorIds = new Set(
        courseMembers
          .filter(member => member.is_instructor)
          .map(member => member.user_id)
      );

      const hasCompletedAccountGame = games.some(game => 
        (game.game_type === 'Account Classification' || game.game_type === 'Normal Balance') &&
        gamesSessions.some(session => session.game === game.id && session.completed === true && !instructorIds.has(session.user))
      );
      if(hasCompletedAccountGame) {
        setPopupMessages(popupMessagesDecisionInsightsClassifyingNormal);
        setDetailsButtonText('View Details');
        setLabels([{name: 'Classification', color: '#D7F0F2'}, {name: 'Normal', color: '#FFF8EB'}]);
      } else {
        setPopupMessages(popupMessagesDecisionInsightsNoGamesData);
      }
    }
  }, [games, gamesSessions, courseMembers]);

  const allDecisionsLoaded = 
    (classificationGameIds.length === 0 || classificationDecisionsQueries.every(query => query.data)) &&
    (normalGameIds.length === 0 || normalDecisionsQueries.every(query => query.data)) &&
    (classificationGameIds.length > 0 || normalGameIds.length > 0);

  const getUniqueQuestions = (allDecisionsData) => {
    return allDecisionsData
      .flat()
      .reduce((acc, item) => {
        const exists = acc.some(existingItem => existingItem.question === item.question);
        if (!exists) {
          acc.push(item);
        }
        return acc;
      }, []);
  };

  const processDecisionData = (decisionInsights, targetHeaders) => {
    return decisionInsights
      .map(decisionInsight => {
        const row = {
          question: decisionInsight.question,
        };

        let totalValue = 0;
        
        // Only process options that match target headers
        const relevantOptions = decisionInsight?.options.filter(option => 
          targetHeaders.includes(option.option)
        );

        // If no relevant options, return null to filter out later
        if (relevantOptions.length === 0) return null;

        relevantOptions.forEach(insightsOption => {
          const optionItem = items.find(item => item.name === insightsOption.option);
          const questionItem = items.find(item => item.name === row.question);
          const isParentMatch = questionItem.parents.includes(optionItem.id);

          row[insightsOption.option] = {
            value: insightsOption.count_users,
            isParentMatch,
          };

          totalValue += insightsOption.count_users;
        });

        relevantOptions.forEach(insightsOption => {
          const itemData = row[insightsOption.option];
          const threshold = Math.min(10, totalValue * 0.2);

          if (itemData.isParentMatch) {
            itemData.background = '#D1E9D6';
          } else if (itemData.value && itemData.value >= threshold) {
            itemData.background = '#FD9595';
          } else {
            itemData.background = '#FFFFFF';
          }
        });

        return row;
      })
      .filter(row => row !== null);
  };


  useEffect(() => {
    if (allDecisionsLoaded && items) {
      const allClassificationDecisionsData = classificationDecisionsQueries.map(query => query.data);
      console.log('allClassificationDecisionsData', allClassificationDecisionsData);
      const uniqueClassificationDecisionInsights = getUniqueQuestions(allClassificationDecisionsData);
      console.log('uniqueClassificationDecisionInsights', uniqueClassificationDecisionInsights);
      
      const elementsData = processDecisionData(uniqueClassificationDecisionInsights, classifyingNormalElementsHeaders);
      const allOtherAccountsData = processDecisionData(uniqueClassificationDecisionInsights, classifyingNormalAllOtherAccountsHeaders);

      setElementsData(elementsData);
      setAllOtherAccountsData(allOtherAccountsData);

      const allNormalDecisionsData = normalDecisionsQueries.map(query => query.data);
      const uniqueNormalInsights = getUniqueQuestions(allNormalDecisionsData);

      const elementsOtherAccountsNormalData = processDecisionData(uniqueNormalInsights, classifyingNormalElementsOtherAccountsNormalHeaders);
      
      setElementsOtherAccountsNormalData(elementsOtherAccountsNormalData);

      setIsModalShown(true);
      setIsDetailsButtonLoading(false);
    }
  }, [allDecisionsLoaded, items]);

  const handlePrevButtonClick = () => {
    navigate('/instructor/insights/decision-insights/overall');
  }

  const handleNextButtonClick = () => {
    let redirectUrl = '/instructor/insights/progress-scores/progress';
    if(games && games.length > 0) {
      if(games.some(game => game.game_type === 'Balanced Equation')) {
        redirectUrl = '/instructor/insights/decision-insights/double-entry';
      } else if(games.some(game => game.game_type === 'The Principles')) {
        redirectUrl = '/instructor/insights/decision-insights/principles';
      }
    }
    navigate(redirectUrl);
  }

  const handleFilterClick = (selectedFilterBy) => {
    setFilterBy(selectedFilterBy);
  }

  const maxScore = Math.max(...data.flatMap(section => 
    section.bars?.flatMap(bar => 
      bar.lines?.map(line => line.score) || []
    ) || []
  ));

  const handleDetailsButtonClick = () => {
    // setIsModalShown(true);

    setIsDetailsButtonLoading(true);
    setShouldFetchDetails(true);
  }

  const handleModalCloseButtonClick = () => {
    setIsModalShown(false);
    
    setClassificationGameIds([]);
    setNormalGameIds([]);
  }

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
      currentPage={2}
      pagesQuantity={4}
      contentClassName="decision-insights-content-container instructor-decision-insights-content-container"
    >
      <h1>INSIGHTS | OVERALL <b>| Account Classification & Normal Balances</b></h1>
      <div className="decision-insights-content classifying-normal-decision-insights-content">
        <PopupMessages
          messages={popupMessages}
          messageSize="xx-small"
          buttonText="Next"
          onButtonClick={handleNextButtonClick}
          additionalButtonText="Back"
          onAdditionalButtonClick={handlePrevButtonClick}
          isAvatarOnTop={true}
        />
        <Scores 
          labels={labels}
          data={data}
          size="small"
          maxScore={maxScore}
          alignBarTitle="center"
          detailsButtonText={detailsButtonText}
          onDetailsButtonClick={handleDetailsButtonClick}
          isDetailsButtonLoading={isDetailsButtonLoading}
        />
        {isModalShown && (
          <InstructorClassifyingNormalModal
            onCloseButtonClick={handleModalCloseButtonClick}
            elementsData={elementsData}
            elementsHeaders={classifyingNormalElementsHeaders}
            allOtherAccountsData={allOtherAccountsData}
            allOtherAccountsHeaders={classifyingNormalAllOtherAccountsHeaders}
            elementsOtherAccountsNormalData={elementsOtherAccountsNormalData}
            elementsOtherAccountsNormalHeaders={classifyingNormalElementsOtherAccountsNormalHeaders}
          />
        )}
      </div>
    </SubPagesLayout>
  );
}
